import React, { useMemo, useCallback } from "react";
import { Collapse, Divider, List } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Phone as PhoneIcon } from "@mui/icons-material";
import SidebarCategory from "./SidebarCategory";
import PlatformSidebarCategory from "./PlatformSidebarCategory";
import SidebarLink from "./SidebarLink";
import PlatformSidebarLink from "./PlatformSidebarLink";
import NavLinkWrapper from "./NavLinkWrapper";
import { useLocation } from "@/compat/router";
import {
    baseRoute as safetynestBaseRoute,
    menuItems as safetynestMenuItems,
} from "../../safetynest/routes";
import useExpandedCategories from "../../hooks/useExpandedCategories";
import { useAuth } from "../../contexts/authContext";
import useIsMobile from "../../hooks/useIsMobile";
import { useSelector } from "react-redux";
import useFeatureFlag from "../../hooks/useFeatureFlag";

const useStyles = makeStyles((theme) => ({
    navigationContainer: {
        flexGrow: 1,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        overflowY: "auto",
    },
    divider: {
        background: "rgba(255, 255, 255, 0.12)",
    },
}));

const contactPhoneNumber = "0345 226 8393";

function DrawerMenuItems() {
    const classes = useStyles();
    const location = useLocation();
    const { expandedCategories, toggleCategory } = useExpandedCategories();
    const { isWorknestUser, hasAnySitesRoles, user } = useAuth();
    const isMobile = useIsMobile();
    const { selectedAccount } = useSelector((state) => state.account);
    const { data: isPlatformEnabled } = useFeatureFlag("Platform");
    const pathStartsWith = (prefix) => location.pathname.startsWith(prefix);
    const usePlatformStyle = (pathStartsWith("/knowledgehub/healthandsafety") || pathStartsWith("/resourcenest") || pathStartsWith("/safetynest") || location.pathname === "/") && isPlatformEnabled ? true : false;


    const userAccessPermissionModules =
        user?.details?.userAccessPermission &&
        safetynestMenuItems
            .filter((module) =>
                !module.hiddenFromClientUser &&
                    !module?.userAccessPermission
                    ? true
                    : user?.details?.userAccessPermission[module.userAccessPermission] ===
                    true
            )
            .map((menuItem) =>
                !menuItem?.children
                    ? menuItem
                    : {
                        ...menuItem,
                        children: menuItem?.children?.filter(
                            (nestedModule) =>
                                user?.details?.userAccessPermission[
                                nestedModule.userAccessPermission
                                ] === true
                        ),
                    }
            );

    const filteredByRouteRoles = useCallback((route) =>
        !route.requiredSiteRoles || hasAnySitesRoles(selectedAccount?.childExternalIds, route.requiredSiteRoles
        ), [hasAnySitesRoles, selectedAccount?.childExternalIds]);

    const menuItemsForPath = useMemo(() => {
        if (location.pathname.startsWith(safetynestBaseRoute))
            return isWorknestUser
                ? safetynestMenuItems.filter((x) => !x.hiddenFromWorkNestUser)
                : userAccessPermissionModules?.filter(filteredByRouteRoles);

        return [];
    }, [filteredByRouteRoles, isWorknestUser, location.pathname, userAccessPermissionModules]);

    return (
        <div className={classes.navigationContainer}>
            <List disablePadding>
                <div>
                    {menuItemsForPath?.map((category, index) => (
                        <React.Fragment key={index}>
                            {category.children ? (
                                <React.Fragment key={index}>
                                    {usePlatformStyle ? (
                                        <PlatformSidebarCategory
                                            isOpen={expandedCategories.includes(index)}
                                            isCollapsable
                                            name={category.label}
                                            icon={category.icon}
                                            onClick={() => toggleCategory(index)}
                                        />) : (
                                        <SidebarCategory
                                            isOpen={expandedCategories.includes(index)}
                                            isCollapsable
                                            name={category.label}
                                            icon={category.icon}
                                            onClick={() => toggleCategory(index)}
                                        />
                                    )}
                                    <Collapse
                                        in={expandedCategories.includes(index)}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        {category.children.map((route, subIndex) => (
                                            usePlatformStyle ? (
                                                <PlatformSidebarLink
                                                    key={subIndex}
                                                    name={route.label}
                                                    to={category.path + route.path}
                                                    icon={route.icon}
                                                    badge={route.badge}
                                                />
                                            ) : (
                                                <SidebarLink
                                                    key={subIndex}
                                                    name={route.label}
                                                    to={category.path + route.path}
                                                    icon={route.icon}
                                                    badge={route.badge}
                                                />
                                            )
                                        ))}
                                    </Collapse>
                                </React.Fragment>
                            ) : (
                                usePlatformStyle ? (
                                    <PlatformSidebarCategory
                                        name={category.label}
                                        to={category.path}
                                        activeClassName="active"
                                        component={NavLinkWrapper}
                                        icon={category.icon}
                                        exact
                                        badge={category.badge}
                                    />
                                ) : (
                                    <SidebarCategory
                                        name={category.label}
                                        to={category.path}
                                        activeClassName="active"
                                        component={NavLinkWrapper}
                                        icon={category.icon}
                                        exact
                                        badge={category.badge}
                                    />
                                )
                            )}
                        </React.Fragment>
                    ))}
                    {isMobile && !usePlatformStyle && (
                        <>
                            <Divider className={classes.divider} />
                            <SidebarCategory
                                name={`Need Help? Call: ${contactPhoneNumber}`}
                                activeClassName="active"
                                component="a"
                                icon={PhoneIcon}
                                exact
                                href={`tel:${contactPhoneNumber}`}
                            />
                        </>
                    )}
                </div>
            </List>
        </div>
    );
}

export default DrawerMenuItems;
